import { memo, useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import TrashIcon from "@mui/icons-material/Delete";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";

import { Bucket, BucketAsset, BucketNumberEnum } from "features/trading/buckets";
import {
  MAIN_STAR_COLOR,
  MemberHistoryStatusEnum,
  memberHistoryStatus,
  sortMembers,
} from "./constants";
import { BucketActions } from "./useBucketActions";

// Buckets bigger than this get a search field (a Bucket 3 can hold
// hundreds of assets).
const SEARCH_MIN_MEMBERS = 10;

type MemberActions = Pick<
  BucketActions,
  "toggleMain" | "setIncluded" | "removeAsset"
>;

// Whether the pair's AccountHistory exists — reports can only run for
// members with a green check. The red state carries the generation error.
const MemberHistoryIcon = ({ member }: { member: BucketAsset }) => {
  switch (memberHistoryStatus(member)) {
    case MemberHistoryStatusEnum.READY:
      return (
        <Tooltip title="Account history exists">
          <CheckCircleOutline fontSize="small" color="success" />
        </Tooltip>
      );
    case MemberHistoryStatusEnum.FAILED:
      return (
        <Tooltip title={`History generation failed: ${member.history_error}`}>
          <ErrorOutline fontSize="small" color="error" />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Account history not generated yet">
          <HourglassEmpty fontSize="small" color="disabled" />
        </Tooltip>
      );
  }
};

interface BucketMemberRowProps {
  member: BucketAsset;
  isBucket1: boolean;
  isBucket3: boolean;
  actions: MemberActions;
  isSelected: boolean;
  // Row click opens/closes the member's account-history panel (Buckets
  // page only — undefined elsewhere).
  onClick?: (member: BucketAsset) => void;
}

// Memoized: with stable `actions` (useBucketActions memoizes them) only the
// rows whose member actually changed re-render — noticeable on large
// Bucket 3 lists, especially while typing in the search.
const BucketMemberRow = memo(
  ({
    member,
    isBucket1,
    isBucket3,
    actions,
    isSelected,
    onClick,
  }: BucketMemberRowProps) => (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      paddingX={1}
      paddingY={0.5}
      onClick={onClick ? () => onClick(member) : undefined}
      sx={{
        ...(isBucket3 && !member.included ? { opacity: 0.45 } : {}),
        ...(onClick ? { cursor: "pointer" } : {}),
        ...(isSelected ? { backgroundColor: "action.selected" } : {}),
      }}
    >
      {isBucket1 && (
        <Tooltip
          title={member.is_main ? "Un-set main asset" : "Make main asset"}
        >
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              actions.toggleMain(member);
            }}
          >
            {member.is_main ? (
              <Star fontSize="small" htmlColor={MAIN_STAR_COLOR} />
            ) : (
              <StarBorder fontSize="small" color="disabled" />
            )}
          </IconButton>
        </Tooltip>
      )}
      {isBucket3 && (
        <Checkbox
          size="small"
          checked={member.included}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) =>
            actions.setIncluded(member, event.target.checked)
          }
        />
      )}
      <Typography
        fontWeight={member.is_main ? 700 : 500}
        color="primary.main"
        minWidth={70}
      >
        {member.symbol}
      </Typography>
      <Typography variant="body2" color="text.secondary" flexGrow={1} noWrap>
        {member.asset__name}
      </Typography>
      {/* Bucket 3 doesn't use AccountHistory — no status to show. */}
      {!isBucket3 && <MemberHistoryIcon member={member} />}
      {!isBucket3 && (
        <Tooltip title="Remove from bucket">
          <IconButton
            size="small"
            color="error"
            aria-label="Remove asset from bucket"
            onClick={(event) => {
              event.stopPropagation();
              actions.removeAsset(member);
            }}
          >
            <TrashIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ),
);

interface MemberSearchFieldProps {
  search: string;
  onSearchChange: (value: string) => void;
  matched: number;
  total: number;
}

const MemberSearchField = ({
  search,
  onSearchChange,
  matched,
  total,
}: MemberSearchFieldProps) => (
  <Box display="flex" alignItems="center" gap={1} paddingX={1} paddingBottom={1}>
    <TextField
      fullWidth
      size="small"
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
      placeholder="Find asset by symbol or name…"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => onSearchChange("")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
      {matched} / {total}
    </Typography>
  </Box>
);

const EmptyState = ({ text }: { text: string }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    textAlign="center"
    sx={{ padding: 3 }}
  >
    {text}
  </Typography>
);

interface BucketMembersListProps {
  bucket: Bucket;
  actions: MemberActions;
  maxHeight?: number;
  // Asset id whose row is highlighted (its history panel is open).
  selectedAssetId?: number | null;
  onMemberClick?: (member: BucketAsset) => void;
}

/**
 * Member rows for a bucket — main-asset star (Bucket 1), include/exclude
 * checkbox (Bucket 3), remove button (Buckets 1/2), and a symbol/name
 * search once the list grows large. Shared between the client-page Buckets
 * tab and the report-settings bucket modals.
 */
export const BucketMembersList = ({
  bucket,
  actions,
  maxHeight,
  selectedAssetId,
  onMemberClick,
}: BucketMembersListProps) => {
  const isBucket1 = bucket.number === BucketNumberEnum.BUCKET_1;
  const isBucket3 = bucket.number === BucketNumberEnum.BUCKET_3;

  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch("");
  }, [bucket.id]);

  const sortedMembers = useMemo(
    () => sortMembers(bucket.assets),
    [bucket.assets],
  );

  const showSearch = bucket.assets.length > SEARCH_MIN_MEMBERS;
  const needle = search.trim().toLowerCase();
  const members = useMemo(() => {
    if (!showSearch || !needle) return sortedMembers;
    return sortedMembers.filter(
      (member) =>
        member.symbol.toLowerCase().includes(needle) ||
        member.asset__name.toLowerCase().includes(needle),
    );
  }, [sortedMembers, needle, showSearch]);

  if (bucket.assets.length === 0) {
    return <EmptyState text="No assets in this bucket yet." />;
  }

  return (
    <Box>
      {showSearch && (
        <MemberSearchField
          search={search}
          onSearchChange={setSearch}
          matched={members.length}
          total={bucket.assets.length}
        />
      )}
      {members.length === 0 ? (
        <EmptyState text="No assets match your search." />
      ) : (
        <Stack
          divider={<Divider />}
          sx={{ maxHeight, overflowY: maxHeight ? "auto" : undefined }}
        >
          {members.map((member) => (
            <BucketMemberRow
              key={member.id}
              member={member}
              isBucket1={isBucket1}
              isBucket3={isBucket3}
              actions={actions}
              isSelected={member.asset === selectedAssetId}
              onClick={onMemberClick}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
